import { SfLog } from '@app/adapters/logger'
import { Tooltip } from '@app/setup/tooltip/uikit-tooltip'
import { Controller } from '@hotwired/stimulus'

import type { Placement } from 'tooltip.js'

const VALID_PLACEMENTS: Placement[] = ['top', 'bottom', 'left', 'right']

function isPlacement(value: string): value is Placement {
  return VALID_PLACEMENTS.includes(value as Placement)
}

const REDACTED_CLASS = 'elements-should-be-redacted'

export default class extends Controller {
  static values = { placement: String, showDelay: Number }

  declare placementValue: string
  declare showDelayValue: number

  private get placement(): Placement {
    return isPlacement(this.placementValue) ? this.placementValue : 'bottom'
  }

  private tooltip: Tooltip | null = null
  private resizeObserver: ResizeObserver | null = null
  private skipTooltip = false

  connect(): void {
    this.skipTooltip =
      !!(this.element as HTMLElement).closest(`.${REDACTED_CLASS}`) ||
      !(this.element.textContent ?? '').trim()

    this.resizeObserver = new ResizeObserver(() => this.refresh())
    this.resizeObserver.observe(this.element)
    this.refresh()
  }

  disconnect(): void {
    this.resizeObserver?.disconnect()
    this.resizeObserver = null
    this.destroyTooltip()
  }

  private refresh = (): void => {
    const element = this.element as HTMLElement

    if (element.scrollWidth > element.offsetWidth) {
      this.createTooltip()
    } else {
      this.destroyTooltip()
    }
  }

  private createTooltip(): void {
    if (this.tooltip || this.skipTooltip) {
      return
    }

    this.tooltip = new Tooltip(this.element as HTMLElement, {
      title: (this.element.textContent ?? '').trim(),
      placement: this.placement,
      size: 'sm',
      delay: { show: this.showDelayValue, hide: 0 },
    })
  }

  private destroyTooltip(): void {
    if (!this.tooltip) {
      return
    }

    try {
      this.tooltip.dispose()
    } catch (error) {
      SfLog.error('Error disposing truncation tooltip', { error })
    }

    this.tooltip = null
  }
}
